import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { listBrokerageBonds, type BrokerageBond } from "@/lib/brokerAccounts";
import { IssuedBondsTable } from "@/components/IssuedBondsTable";
import { messageFor } from "@/lib/errors";

// BRK-S181 revision 7 — the Surety nav's BONDS: what Signal has actually
// ISSUED, across every insured the brokerage owns (`GET /api/broker/bonds`,
// PMS-S479 revision 2).
//
// ── NOT THE ORDERS LIST ──────────────────────────────────────────────────────
//
// The nav's "Bonds" used to open `/surety/submissions`, the bond-ORDER list,
// while "Submissions" was a dead label — Aaron's rule is that Submissions lists
// submissions (bond requests) and Bonds lists issued bonds. That list keeps its
// route and is now the Submissions item; this page is the bonds themselves, by
// the number an obligee quotes back. Relabelling the orders list as Bonds would
// have been the confusion being fixed.
//
// The row is the account Bonds tab's (BRK-S175) plus its insured, rendered by
// the SAME `IssuedBondsTable`, so a bond reads identically on both. Paged by
// the route (`limit` 1–100); "Show more" appends the next page rather than
// pretending the first page is the whole book.

const PAGE_SIZE = 50;

export function SuretyBondsPage() {
  const { authedRequest } = useAuth();
  const [bonds, setBonds] = useState<BrokerageBond[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const page = await listBrokerageBonds(authedRequest, {
          limit: PAGE_SIZE,
        });
        if (cancelled) return;
        setBonds(page.items);
        setTotal(page.total);
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  const showMore = useCallback(async () => {
    if (!bonds) return;
    setLoadingMore(true);
    setError(null);
    try {
      const page = await listBrokerageBonds(authedRequest, {
        limit: PAGE_SIZE,
        offset: bonds.length,
      });
      setBonds([...bonds, ...page.items]);
      setTotal(page.total);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setLoadingMore(false);
    }
  }, [authedRequest, bonds]);

  return (
    <section className="flex w-full flex-col gap-6 py-8">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight">Bonds</h1>
        <p className="text-brk-dim">
          The bonds Signal has issued for your brokerage’s insureds.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
      )}

      {bonds === null && !error && (
        <p role="status" className="text-sm text-brk-dim">
          Loading bonds…
        </p>
      )}

      {bonds !== null && bonds.length === 0 && (
        // HONEST, and with nothing to click: a broker cannot go and issue a
        // bond. The way to one is a request, which lives under Submissions.
        <div className="rounded-xl border border-dashed border-brk-border bg-brk-panel px-4 py-10">
          <p className="text-sm text-brk-text">No bonds issued yet.</p>
        </div>
      )}

      {bonds !== null && bonds.length > 0 && (
        <div className="flex flex-col gap-3">
          <IssuedBondsTable
            bonds={bonds}
            accountIdOf={(bond) => bond.accountId}
            showInsured
          />
          {bonds.length < total && (
            <button
              type="button"
              onClick={() => void showMore()}
              disabled={loadingMore}
              className="self-start rounded-md border border-brk-border px-4 py-2 text-sm font-medium text-brk-text transition-colors hover:bg-brk-bg disabled:opacity-60"
            >
              {loadingMore ? "Loading…" : "Show more"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
